import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ModalTitle } from 'components';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import faxQueuedSound from 'assets/audio/fax/fax-queued.mp3';

import styles from './SendFaxDialog.module.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  onSend: (payload: { files: File[]; faxNumber: string; subject: string }) => Promise<void>;
};

const formatFaxNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const SendFaxDialog = ({ open, onClose, onSend }: Props) => {
  const { t } = useTranslation('settings');
  const [files, setFiles] = useState<File[]>([]);
  const [faxNumber, setFaxNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const SUBJECT_OPTIONS = [
    t('sendFaxDialog.completedIntake'),
    t('sendFaxDialog.outstandingDetentionOrder'),
    t('sendFaxDialog.signedPetitions'),
    t('sendFaxDialog.alerts'),
    t('sendFaxDialog.clerkPaperwork'),
    t('sendFaxDialog.custom'),
  ] as const;

  const [subjectOption, setSubjectOption] = useState<string>(t('sendFaxDialog.completedIntake'));
  const [customSubject, setCustomSubject] = useState('');

  const resolvedSubject = subjectOption === t('sendFaxDialog.custom') ? customSubject.trim() : subjectOption;

  const faxQueuedAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    faxQueuedAudioRef.current = new Audio(faxQueuedSound);
  }, []);

  const canSubmit = Boolean(
    files.length > 0 &&
      faxNumber.replace(/\D/g, '').length === 10 &&
      (subjectOption !== t('sendFaxDialog.custom') || customSubject.trim().length > 0),
  );

  const resetForm = () => {
    setFiles([]);
    setFaxNumber('');
    setSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSend = async () => {
    if (files.length === 0 || !faxNumber || submitting) return;

    setSubmitting(true);

    try {
      // ⏳ Wait for backend to accept the fax
      await onSend({
        files,
        faxNumber,
        subject: resolvedSubject,
      });

      // ✅ SUCCESS ONLY — play sound now
      try {
        if (faxQueuedAudioRef.current) {
          faxQueuedAudioRef.current.currentTime = 0;
          faxQueuedAudioRef.current.muted = false;
          await faxQueuedAudioRef.current.play();
        }
      } catch (err) {
        console.debug('Fax queued sound blocked', err);
      }

      // ✅ Close dialog AFTER success + sound
      handleClose();
    } catch (err) {
      // ❌ Failure: do NOT play sound, do NOT close dialog
      console.error('Send fax failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title={t('sendFaxDialog.sendFax')} onClose={handleClose} />
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {/* File Drop Zone */}
          <Box
            className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''}`}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // Only reset if leaving the drop zone entirely
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragActive(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);

              const droppedFiles = Array.from(e.dataTransfer.files || []);
              if (droppedFiles.length > 0) {
                setFiles((prev) => [...prev, ...droppedFiles]);
              }
            }}
          >
            <div className={styles.dropZoneContent}>
              <CloudUploadOutlinedIcon className={styles.uploadIcon} />

              {files.length === 0 ? (
                // EMPTY STATE — keep everything centered
                <Stack alignItems='center' spacing={2} sx={{ width: '100%' }}>
                  <Typography className={styles.primaryText}>{t('sendFaxDialog.dragAndDropFilesHere')}</Typography>

                  <Button
                    variant='outlined'
                    component='label'
                    size='small'
                    className={styles.actionButton}
                    sx={{ width: 'auto' }} // 👈 prevent full-width button
                  >
                    {t('sendFaxDialog.selectFiles')}
                    <input
                      type='file'
                      hidden
                      multiple
                      accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.tif,.tiff,.txt'
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        setFiles((prev) => [...prev, ...selected]);
                      }}
                    />
                  </Button>
                </Stack>
              ) : (
                // FILE LIST STATE — full width rows, left/right aligned
                <>
                  <Stack sx={{ width: '100%' }}>
                    {files.map((f, idx) => (
                      <Stack
                        key={idx}
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{
                          width: '100%',
                          margin: '0 0 10px 0',
                        }}
                      >
                        <Typography className={styles.fileName}>{f.name}</Typography>

                        <Typography
                          className={styles.removeText}
                          onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          {t('sendFaxDialog.remove')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Button
                    variant='outlined'
                    component='label'
                    size='small'
                    className={styles.actionButton}
                    sx={{ width: 'auto' }} // 👈 keeps button compact
                  >
                    {t('sendFaxDialog.addMoreFiles')}
                    <input
                      type='file'
                      hidden
                      multiple
                      accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.tif,.tiff,.txt'
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        setFiles((prev) => [...prev, ...selected]);
                      }}
                    />
                  </Button>
                </>
              )}
            </div>
          </Box>

          {/* Fax Number */}
          <div className={styles.fieldGroup}>
            <Typography variant='body2' fontWeight={600}>
              {t('sendFaxDialog.faxToNumber')}
            </Typography>

            <TextField
              placeholder={t('sendFaxDialog.faxNumberPlaceholder')}
              value={faxNumber}
              onChange={(e) => setFaxNumber(formatFaxNumber(e.target.value))}
              fullWidth
            />
          </div>

          <div className={styles.fieldGroup}>
            <Typography variant='body2' fontWeight={600}>
              {t('sendFaxDialog.subject')}
            </Typography>

            <Stack spacing={1}>
              <Select size='small' value={subjectOption} onChange={(e) => setSubjectOption(e.target.value)} fullWidth>
                {SUBJECT_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>

              {subjectOption === t('sendFaxDialog.custom') && (
                <TextField
                  size='small'
                  placeholder={t('sendFaxDialog.enterCustomSubject')}
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  fullWidth
                  autoFocus
                />
              )}
            </Stack>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <Button variant='outlined' fullWidth onClick={handleClose} disabled={submitting}>
              {t('sendFaxDialog.cancel')}
            </Button>

            <Button
              variant='contained'
              fullWidth
              disabled={!canSubmit || submitting}
              onClick={handleSend}
              sx={{
                position: 'relative',
              }}
            >
              {submitting ? (
                <Stack direction='row' alignItems='center' spacing={1}>
                  <CircularProgress size={18} color='inherit' />
                  <span>{t('sendFaxDialog.sending')}</span>
                </Stack>
              ) : (
                t('sendFaxDialog.sendFax')
              )}
            </Button>
          </div>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SendFaxDialog;
